<script setup>
import BaseDateSelect from '../base/BaseDateSelect.vue'
import BaseSelectCustomer from '../base/BaseSelectCustomer.vue'

import {
  computed,
  ref,
  watch,
  defineProps,
  defineEmits,
  defineExpose
} from 'vue'
import { getCurrentDay } from '@/utils/DateHelpers'
import { formatCurrency, formatDecimals } from '@/helpers/stringHelpers'
import { useStore } from '@/utils/VuexHelpers'

const props = defineProps({
  value: {
    type: Boolean,
    default: false
  },

  selectedProducts: {
    type: Array,
    default: () => []
  },

  customerRequired: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['input', 'save'])

const store = useStore()
const loadingProdPrice = computed(() => store.getters.loadingProdPrice)

const customerSelectRef = ref(null)
const effectiveDate = ref(getCurrentDay())
const endDate = ref('')
const comment = ref('')
const adjustmentType = ref('amount')
const adjustmentValue = ref(null)
const priceRows = ref([])
const selectedCustomerIds = ref([])

const adjustmentTypeOptions = [
  {
    text: 'Increase by amount',
    value: 'amount'
  },
  {
    text: 'Increase by percentage',
    value: 'percentage'
  }
]

const headers = [
  {
    text: 'Product',
    value: 'name',
    sortable: true
  },
  {
    text: 'Code',
    value: 'code',
    sortable: true,
    width: '120px'
  },
  {
    text: 'Standard Price/Kg',
    value: 'currentPrice',
    sortable: false,
    align: 'end'
  },
  {
    text: 'New Price/Kg',
    value: 'newPrice',
    sortable: false,
    align: 'end',
    width: '160px'
  },
  {
    text: 'Difference',
    value: 'difference',
    sortable: false,
    align: 'end'
  }
]

const dialog = computed({
  get() {
    return props.value
  },
  set(value) {
    emit('input', value)
  }
})

const hasRows = computed(() => priceRows.value.length > 0)

const changedRows = computed(() => {
  return priceRows.value.filter((row) => {
    if (row.newPrice === null) return false

    const value = Number(row.newPrice)
    if (Number.isNaN(value)) return false

    return value !== Number(row.currentPrice)
  })
})

const hasChanges = computed(() => changedRows.value.length > 0)

const isValid = computed(() => {
  return (
    !!effectiveDate.value &&
    hasRequiredCustomer.value &&
    priceRows.value.length > 0 &&
    priceRows.value.every((row) => {
      if (row.newPrice === null || row.newPrice === '') return true
      return Number(row.newPrice) >= 0
    })
  )
})

const customerSelectRules = computed(() => {
  if (!props.customerRequired) return []

  return [
    (v) => {
      if (Array.isArray(v)) return v.length > 0 || 'Customer is required'
      return !!v || 'Customer is required'
    }
  ]
})

const hasRequiredCustomer = computed(() => {
  if (!props.customerRequired) return true

  return (
    Array.isArray(selectedCustomerIds.value) &&
    selectedCustomerIds.value.length > 0
  )
})

function formatDifference(row) {
  if (row.newPrice === null || row.newPrice === '') return '—'

  const difference = Number(row.newPrice) - Number(row.currentPrice || 0)
  const sign = difference > 0 ? '+' : ''

  return `${sign}${formatCurrency(difference)}`
}

function getDifferenceClass(row) {
  if (row.newPrice === null || row.newPrice === '') return ''

  const difference = Number(row.newPrice) - Number(row.currentPrice || 0)

  if (difference > 0) return 'success--text'
  if (difference < 0) return 'error--text'

  return ''
}

function applyAdjustment() {
  const value = Number(adjustmentValue.value)

  if (adjustmentValue.value === null || adjustmentValue.value === '') return

  priceRows.value = priceRows.value.map((row) => {
    const currentPrice = Number(row.currentPrice || 0)
    let newPrice = currentPrice

    if (adjustmentType.value === 'amount') {
      newPrice = currentPrice + value
    }

    if (adjustmentType.value === 'percentage') {
      newPrice = currentPrice + currentPrice * (value / 100)
    }

    return {
      ...row,
      newPrice: formatDecimals(newPrice)
    }
  })
}

function resetNewPrices() {
  priceRows.value = priceRows.value.map((row) => ({
    ...row,
    newPrice: null
  }))

  adjustmentValue.value = null
}

function closeDialog() {
  dialog.value = false
}

function saveChanges() {
  if (!isValid.value || !hasChanges.value) return

  emit('save', {
    effectiveDate: effectiveDate.value,
    endDate: endDate.value,
    comment: comment.value,
    customerIds: selectedCustomerIds.value,
    items: changedRows.value.map((row) => ({
      productId: row.productId,
      oldPriceKg: Number(row.currentPrice),
      newPriceKg: Number(row.newPrice)
    }))
  })
}

const resetForm = () => {
  effectiveDate.value = getCurrentDay()
  endDate.value = ''
  comment.value = ''
  selectedCustomerIds.value = []
  customerSelectRef.value?.resetValidation()
  adjustmentType.value = 'amount'

  resetNewPrices()
}

watch(
  () => props.selectedProducts,
  (products) => {
    priceRows.value = products.map((product) => {
      const { id, product: name, code, default_price } = product

      const currentPrice = default_price ?? 0

      return {
        productId: id,
        name,
        code,
        currentPrice,
        newPrice: null
      }
    })
  },
  {
    immediate: true,
    deep: true
  }
)

defineExpose({
  resetForm
})
</script>

<template>
  <v-dialog v-model="dialog" max-width="1200px" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-start flex-column">
          <h6
            class="text-button text-light-blue text-start mb-0"
            style="line-height: 1.5rem"
          >
            Batch Update Product Prices
          </h6>
          <div class="text-body-2 grey--text text--darken-1">
            {{ priceRows.length }} selected products
          </div>
        </div>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="mt-0" />

      <v-card-text>
        <v-alert type="info" dense text class="mb-2">
          Set one effective date for this batch, then apply a common increase or
          manually edit each product price.
        </v-alert>

        <v-card outlined class="mb-2">
          <v-card-text class="pb-3">
            <div class="subtitle-2 mb-2">
              Batch details
              <span class="text-caption grey--text text--darken-1"
                >(optional)</span
              >
            </div>

            <v-row dense>
              <v-col cols="12" md="4">
                <BaseSelectCustomer
                  ref="customerSelectRef"
                  label="Apply to Customers"
                  :customer="selectedCustomerIds"
                  :multiple="true"
                  :show-phone-number="false"
                  :rules="customerSelectRules"
                  @update:customer="selectedCustomerIds = $event"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="comment"
                  label="Comment"
                  outlined
                  dense
                  clearable
                  hide-details
                />
              </v-col>

              <v-col cols="12" md="4">
                <BaseDateSelect
                  :date="endDate"
                  label="End Date"
                  :min-date="effectiveDate"
                  min-date-error-message="Date should be the same or later than the Effective Day."
                  :clearable="true"
                  @update:date="
                    (newDate) => {
                      endDate = newDate
                    }
                  "
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card outlined class="mb-2">
          <v-card-text>
            <div class="subtitle-2 mb-2">
              Apply price change to all selected products
            </div>

            <v-row align="baseline">
              <v-col cols="12" md="3">
                <v-select
                  v-model="adjustmentType"
                  :items="adjustmentTypeOptions"
                  :menu-props="{
                    bottom: true,
                    offsetY: true,
                    contentClass: `mt-1`
                  }"
                  label="Change Type"
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="adjustmentValue"
                  :label="adjustmentType === 'amount' ? 'Amount' : 'Percentage'"
                  :prefix="adjustmentType === 'amount' ? '$' : undefined"
                  :suffix="adjustmentType === 'percentage' ? '%' : undefined"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <v-col cols="12" md="3" class="d-flex align-center">
                <v-btn
                  small
                  color="primary"
                  class="mr-2"
                  :disabled="!hasRows"
                  @click="applyAdjustment"
                >
                  Apply
                </v-btn>

                <v-btn
                  small
                  color="error"
                  text
                  :disabled="!hasRows"
                  @click="resetNewPrices"
                >
                  Reset
                </v-btn>
              </v-col>

              <v-col cols="12" md="3" class="pb-0">
                <BaseDateSelect
                  :date="effectiveDate"
                  :max-date="endDate"
                  label="Effective Date"
                  @update:date="
                    (newDate) => {
                      effectiveDate = newDate
                    }
                  "
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-data-table
          :headers="headers"
          :items="priceRows"
          item-key="productId"
          sort-by="code"
          dense
          hide-default-footer
          disable-pagination
          class="elevation-1 batch-price-table"
        >
          <template #[`item.currentPrice`]="{ item }">
            <div class="text-right">
              {{ formatCurrency(item.currentPrice) }}
            </div>
          </template>

          <template #[`item.newPrice`]="{ item }">
            <v-text-field
              v-model="item.newPrice"
              type="number"
              step="0.01"
              min="0"
              prefix="$"
              dense
              outlined
              hide-details
              class="price-input"
            />
          </template>

          <template #[`item.difference`]="{ item }">
            <div class="text-right" :class="getDifferenceClass(item)">
              {{ formatDifference(item) }}
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider />

      <v-card-actions class="justify-space-between">
        <div class="text-body-2 grey--text text--darken-1">
          {{ changedRows.length }} products will be updated
        </div>

        <div>
          <v-btn
            :disabled="loadingProdPrice === 'batch-create'"
            color="error"
            text
            @click="closeDialog"
            class="me-2"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            :disabled="!isValid || !hasChanges"
            @click="saveChanges"
            :loading="loadingProdPrice === 'batch-create'"
          >
            Save Price Updates
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.batch-price-table {
  border: 1px solid #e0e0e0;
}

.price-input {
  max-width: 130px;
  margin-left: auto;
}
</style>

<style>
.price-input .v-input__slot {
  min-height: 34px !important;
  margin-top: 0.25rem !important;
  margin-bottom: 0.25rem !important;
}
</style>
